import brhMainTemplate from './brhMain.html';

/*******************************************************************************
 * StatementList */
/**
 * @ngdoc   component
 * @module  bankReconHistory
 * @name    bankReconHistory.component:statementList
 * @description
 * Displays a list of Bank Statements for the organization. The statements are
 * grouped in the grid by the bank account, and if a defaul bank account has been
 * set, the default group will be opened upon ap initialization. Each row will
 * show important information about the bank statement, such as the starting and
 * ending balances, as well as the cleared and register balances. An action
 * column contains buttons for viewing more detail, printing the reconciliation,
 * and dropping the reconciliation (if it is the most recent, reconciled
 * statement for the account).
 *******************************************************************************/
export const statementListComponent = {
  template: brhMainTemplate,
  controller: StatementListController
};

/*******************************************************************************
 * StatementListController */
/**
 * Controller for the {@link bankReconHistory.component:statementList
 * statementList} component.
 *******************************************************************************/
/* @ngInject */ function StatementListController(
  $location,
  Alert,
  BankReconHistoryService,
  BankReconService,
  BusyScreen,
  PrintStatementDialog,
  SERVER,
  uiGridConstants
) {
  /*****************************************************************************
   * Properties
   *****************************************************************************/
  /** Controller Instance Reference. */ var mThis = this;
  /** Print dialog is open. */ var mPrintDialogOpen = false;

  /*-----------*/
  /* App Flags */
  /*-----------*/
  /** Restrict statement removal. */
  mThis.preventRemove = !!SERVER.appFlags.restrictReconRemove;

  /*----------------------------------------------------*/
  /* Template to add double click functionality to each */
  /* grid row. Begins editing the double clicked event. */
  /*----------------------------------------------------*/
  var dblClickTemplate =
    '<div ng-dblclick="grid.appScope.viewStatement(row.entity.id)" ng-repeat="(colRenderIndex, col) in colContainer.renderedColumns track by col.colDef.name" class="ui-grid-cell marker" ui-grid-cell></div>';

  /*------------------------------------------------*/
  /* Template for the actions column with icons to  */
  /* edit event, manage attendees and delete event. */
  /*------------------------------------------------*/
  var actionsCellTemplate =
    '<div class="ui-grid-cell-contents ng-binding ng-scope" ng-show="row.entity.id"> ' +
    '<span class="pointer glyphicon glyphicon-search csm-grid-space-target" title="View Statement"  ng-click="grid.appScope.$ctrl.viewStatement(row.entity.id)"></span> &nbsp;' +
    '<span class="pointer glyphicon glyphicon-print  csm-grid-space-target" title="Print Statement" ng-click="grid.appScope.$ctrl.printStatement(row.entity.id)"></span> &nbsp;' +
    '<span class="pointer glyphicon glyphicon-trash  csm-grid-space-target" title="Drop Statement"  ng-click="grid.appScope.$ctrl.removeStatement(row.entity.id)" ng-show="!grid.appScope.$ctrl.preventRemove && row.entity.canDrop"></span> &nbsp;' +
    '</div>';

  /*--------------*/
  /* Grid Options */
  /*--------------*/
  mThis.dgStatements = {
    enableColumnMenus: false,
    enableFiltering: false,
    enableFullRowSelection: true,
    enableRowHeaderSelection: false,
    enableSorting: false,
    enableHorizontalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
    onRegisterApi: function (gridApi) {
      mThis.dgStatements.api = gridApi;
    },
    groupingShowCounts: false,
    rowTemplate: dblClickTemplate,
    multiSelect: false,
    data: [],
    columnDefs: [
      /*--------------*/
      /* Bank Account */
      /*--------------*/
      {
        field: 'acctName',
        enableSorting: true,
        namedisplayName: 'Bank Account',
        minWidth: 200,
        grouping: { groupPriority: 1 }
      },
      /*------*/
      /* Date */
      /*------*/
      {
        field: 'entryDate',
        displayName: 'Date',
        cellFilter: 'date:"MM/dd/yyyy"',
        minWidth: 90
      },
      /*----------*/
      /* Starting */
      /*----------*/
      {
        field: 'begBalance',
        type: 'number',
        displayName: 'Starting',
        cellFilter: 'number:2',
        minWidth: 100
      },
      /*---------*/
      /* Cleared */
      /*---------*/
      {
        field: 'clrBalance',
        type: 'number',
        displayName: 'Cleared',
        cellFilter: 'number:2',
        minWidth: 100
      },
      /*--------*/
      /* Ending */
      /*--------*/
      {
        field: 'endBalance',
        type: 'number',
        displayName: 'Ending',
        cellFilter: 'number:2',
        minWidth: 100
      },
      /*----------*/
      /* Register */
      /*----------*/
      {
        field: 'regBalance',
        type: 'number',
        displayName: 'Register',
        cellFilter: 'number:2',
        minWidth: 100
      },
      /*---------*/
      /* Actions */
      /*---------*/
      {
        cellTemplate: actionsCellTemplate,
        enableSorting: false,
        field: 'actions',
        width: 80,
        displayName: 'Actions',
        pinnedRight: true
      }
    ]
  };

  /*------------------------------------*/
  /* Set the cellClass for all columns. */
  /*------------------------------------*/
  mThis.dgStatements.columnDefs.forEach(function (col) {
    col.cellClass = col.type == 'dummy' ? null : getCellClass;
  });

  /*****************************************************************************
   * getCellClass */
  /**
   * Returns the CSS class name(s) to be applied to the grid cells.
   *
   * @param  grid  ui-grid reference
   * @param  row   ui-grid row
   * @param  col   ui-grid column
   * @return       CSS class name inactive or empty string.
   *****************************************************************************/
  function getCellClass(grid, row, col) {
    var classes = [];
    var statement = row.entity;

    /*-------------------------------------------*/
    /* Only set the inactive class if the row    */
    /* is a statement, and it is not reconciled. */
    /*-------------------------------------------*/
    if (statement.id && !statement.reconciled) classes.push('inactive-content');

    if (col.colDef.type == 'money') classes.push('accounting');

    if (col.field == 'clrBalance' || col.field == 'endBalance')
      classes.push(
        statement.clrBalance == statement.endBalance ? 'match' : 'unmatch'
      );

    return classes.join(' ');
  }

  /*****************************************************************************
   * loadStatementList */
  /**
   * Loads the list of statements for the organization.
   *****************************************************************************/
  function loadStatementList() {
    return BankReconHistoryService.viewList().then(function (data) {
      if (!data || data.length == 0) {
        Alert.info('Could not find any bank recon history', 'Bank Recon');
        return (mThis.dgStatements.data = []);
      }

      mThis.regBalanceStmt = null;
      /*--------------------------------------------------------------------------------------------*/
      /* If there is a bank reconciliation in progress the Register amount on this page may not     */
      /* match the Register amount in the bank reconciliation. When this happens we need to display */
      /* a message to the user that the Register amount may not be accurate. Since the organization */
      /* can have multiple bank accounts and there can be multiple bank reconciliations in          */
      /* progress, we need to display a message that is appropriate for the situation. Here are the */
      /* possible scenarios:                                                                        */
      /* 1) One bank account and there is a reconciliation in progress for that account.            */
      /* 2) Multiple bank accounts and multiple bank reconciliations in progress.                   */
      /* 3) Multiple bank accounts and 1 bank reconciliation in progress.                           */
      /* See https://graystep.atlassian.net/wiki/spaces/DEV/pages/1955495938/Bank+recon+history     */
      /* and CS-5514, GPP-232 for more information regarding this.                                  */
      /*--------------------------------------------------------------------------------------------*/

      const hasReconInProgress = data.some((statement) => {
        return !statement.reconciled;
      });
      const firstBankAccountID = data[0].accountID;
      const hasMultipleAccounts = data.some((statement) => {
        return statement.accountID !== firstBankAccountID;
      });
      mThis.reconStatement = `Reconciling this account will rectify any Register balance issues on this page.`;

      if (hasReconInProgress) {
        if (hasMultipleAccounts) {
          const accountsInProgress = data.filter((statement) => {
            return !statement.reconciled;
          });
          if (accountsInProgress.length > 1) {
            mThis.regBalanceStmt = `There are bank reconciliations in progress for ${accountsInProgress.length} 
            accounts (first row of each account is italicized). 
            This may affect the Register balances on this page for those accounts.`;
            mThis.reconStatement = `Reconciling these accounts will rectify any Register balance issues on this page.`;
          } else {
            mThis.regBalanceStmt = `There is a bank reconciliations in progress for ${accountsInProgress[0].acctName}. 
            This may affect the Register balances on this page for this account.`;
          }
        } else {
          mThis.regBalanceStmt = `There is a reconciliation in progress for this account. 
          This may affect the Register balances on this page.`;
        }
      }

      return (mThis.dgStatements.data = data);
    });
  }

  /*****************************************************************************
   * $onInit */
  /**
   * @ngdoc     method
   * @module    bankReconHistory
   * @methodOf  bankReconHistory.controller:StatementListController
   * @name      $onInit
   * @description
   * Initializes the application by loading the list of statements and opening
   * the default bank account group.
   *****************************************************************************/
  mThis.$onInit = function () {
    /*--------------------*/
    /* Initialize the app */
    /*--------------------*/
    BusyScreen.promise(
      loadStatementList().then(function () {
        BankReconHistoryService.getDefaultBank().then(function (defaultBank) {
          if (!defaultBank) return;

          /*-------------------------*/
          /* Open the default group. */
          /*-------------------------*/
          var group =
            mThis.dgStatements.api.grid.grouping.groupingHeaderCache[
              defaultBank.accountName
            ];

          /*---------------------------------------------------------------*/
          /* If the group was not found, it means that the default account */
          /* is no longer active and it will not appear in the grid.       */
          /*---------------------------------------------------------------*/
          if (group) mThis.dgStatements.api.treeBase.expandRow(group.row);
        });
      })
    );
  };

  /*****************************************************************************
   * printStatement */
  /**
   * @ngdoc     method
   * @module    bankReconHistory
   * @methodof  bankReconHistory.controller:StatementListController
   * @name      printStatement
   *
   * @param  statementID  Bank Statement to print.
   *
   * @description
   * Handles the print action. The print dialog will be displayed to allow
   * selection of the report type and detail option.
   *****************************************************************************/
  mThis.printStatement = function (statementID) {
    if (!statementID || mPrintDialogOpen) return;

    mPrintDialogOpen = true;
    PrintStatementDialog.show(statementID).finally(function () {
      mPrintDialogOpen = false;
    });
  };

  /*****************************************************************************
   * removeStatement */
  /**
* @ngdoc     method
* @module    bankReconHistory
* @methodof  bankReconHistory.controller:StatementListController
* @name      removeStatement
* 
* @param  {Number}  statementID  Bank statement to drop.

* @description
* Handles the drop action. After the statement has successfully been dropped,
* reload the list of statements.
*****************************************************************************/
  mThis.removeStatement = function (statementID) {
    /*------------------------*/
    /* Check for a selection. */
    /*------------------------*/
    if (!statementID) {
      Alert.info('A selection is needed for the remove.', 'No Selection');
      return;
    }

    /*----------------------*/
    /* Confirm the removal. */
    /*----------------------*/
    Alert.confirm(
      'Are you sure you want to do undo this bank reconciliation?',
      'Removal Confirm'
    ).then(function (resp) {
      BusyScreen.promise(
        BankReconService.dropStatement(statementID).then(
          function () {
            /*------------------------*/
            /* Reload the root route. */
            /*------------------------*/
            return loadStatementList().then(function () {
              Alert.success('Bank Reconciliation has been removed.');
            });
          },
          function (err) {
            Alert.error(
              err.parsedResponse.message() ||
                'Error removing the bank reconciliation',
              'Failed to remove statement'
            );
          }
        )
      );
    });
  };

  /*****************************************************************************
   * viewStatement */
  /**
   * @ngdoc     method
   * @module    bankReconHistory
   * @methodof  bankReconHistory.controller:StatementListController
   * @name      viewStatement
   *
   * @param  {Object}  statement  Bank statement to view details of.
   *
   * @description
   * Handles the view action. Changes the route to the bankReconHistoryView
   * component.
   *****************************************************************************/
  mThis.viewStatement = function (statementID) {
    if (!statementID) return;

    $location.path('/view/' + statementID);
  };
}
